import React, { useState } from 'react';
import { api } from '../api';
import { tg } from '../telegram';
import { CameraCapture } from '../components/CameraCapture';
import { ConfirmView, ConfirmItem } from '../components/ui/ConfirmView';
import { SuccessView } from '../components/ui/SuccessView';
import { ItemDropdown } from '../components/ui/ItemDropdown';
import { UnitDropdown } from '../components/ui/UnitDropdown';
import { AddWasteItemModal } from '../components/ui/AddWasteItemModal';
import { useSubmitAction } from '../hooks/useSubmitAction';
import { useMasterData } from '../hooks/useMasterData';
import { formatQty } from '../utils/format';
import { toComparisonKey } from '../utils/textNormalizer';
import toast from 'react-hot-toast';
import {
  Camera,
  Upload,
  Plus,
  Trash2,
  RefreshCw,
  ArrowLeft,
  ArrowRight,
  ShieldAlert,
} from 'lucide-react';

interface Props {
  onBack: () => void;
  onSuccess: () => void;
}

interface WasteItemRow {
  item_name: string;
  quantity: number;
  unit: string;
  reason?: string;
}

export const WasteScreen: React.FC<Props> = ({ onBack, onSuccess }) => {
  const { ingredients, rawIngredients, units } = useMasterData();
  const [step, setStep] = useState<'INPUT' | 'CONFIRM' | 'SUCCESS'>('INPUT');
  const [warehouse, setWarehouse] = useState<'STORE' | 'CENTRAL'>('STORE');
  const [items, setItems] = useState<WasteItemRow[]>([]);
  const [note, setNote] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  const handleOcr = async (base64Image: string) => {
    setIsScanning(true);
    tg.haptic.impact('medium');
    const toastId = toast.loading('AI đang phân tích phiếu hủy hỏng...');

    try {
      const res = await api.scanWasteOcr(base64Image);
      if (res.items && res.items.length > 0) {
        // Kho Tổng không có BTP -> Lọc bỏ BTP nếu hủy tại Kho Tổng
        const validItems = warehouse === 'CENTRAL'
          ? res.items.filter((it) => {
              const itKey = toComparisonKey(it.item_name);
              const matchMeta = ingredients.find(
                (ing) => toComparisonKey(ing.name) === itKey || ing.name.toLowerCase() === it.item_name.toLowerCase()
              );
              return !matchMeta || matchMeta.type !== 'PREPARED';
            })
          : res.items;

        if (validItems.length < res.items.length) {
          toast('Đã lọc bỏ các món Bán thành phẩm vì Kho Tổng chỉ quản lý Nguyên liệu thô.', { icon: 'ℹ️' });
        }

        setItems(
          validItems.map((it) => ({
            item_name: it.item_name,
            quantity: it.quantity,
            unit: it.unit || 'g',
            reason: it.reason || 'Hỏng / Hết hạn',
          }))
        );
        toast.success(`Đã nhận diện ${validItems.length} món hủy hỏng`, { id: toastId });
      } else {
        toast.error('Không tìm thấy danh sách món hủy từ ảnh', { id: toastId });
      }
    } catch (err: any) {
      toast.error(`Lỗi OCR: ${err.message}`, { id: toastId });
    } finally {
      setIsScanning(false);
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1];
      handleOcr(base64);
    };
    reader.readAsDataURL(file);
  };

  const addItem = () => {
    tg.haptic.impact('light');
    setIsAddModalOpen(true);
  };

  const handleAddItem = (newItem: WasteItemRow) => {
    setItems((prev) => [...prev, newItem]);
    toast.success(`Đã thêm "${newItem.item_name}" (${newItem.quantity} ${newItem.unit}) vào danh sách hủy`);
  };

  const removeItem = (idx: number) => {
    tg.haptic.selectionChanged();
    setItems((prev) => prev.filter((_, i) => i !== idx));
  };

  const updateItem = (idx: number, field: keyof WasteItemRow, val: any) => {
    setItems((prev) =>
      prev.map((row, i) => {
        if (i !== idx) return row;
        if (field === 'item_name') {
          const match = ingredients.find((m) => m.name === val);
          return {
            ...row,
            item_name: val,
            unit: match ? match.unit : row.unit,
          };
        }
        return { ...row, [field]: val };
      })
    );
  };

  // Submit action hook
  const { isSubmitting, isDisabled, execute: submitWaste } = useSubmitAction(
    async () => {
      const valid = items.filter((it) => it.item_name.trim() && it.quantity > 0);
      await api.recordWaste(valid, warehouse, note);
    },
    {
      successMessage: 'Đã lưu biên bản hủy/hỏng thành công!',
      onSuccess: () => {
        setStep('SUCCESS');
      },
    }
  );

  const goToConfirm = () => {
    const valid = items.filter((it) => it.item_name.trim() && it.quantity > 0);
    if (valid.length === 0) {
      toast.error('Vui lòng chọn ít nhất 1 món và số lượng hủy > 0');
      return;
    }
    tg.haptic.impact('light');
    setStep('CONFIRM');
  };

  // 1. Success Step
  if (step === 'SUCCESS') {
    return (
      <SuccessView
        title="Báo Hủy Thành Công!"
        message={`Đã ghi nhận ${items.filter((it) => it.item_name.trim() && it.quantity > 0).length} món hủy/hỏng vào hệ thống và trừ kho tương ứng.`}
        onDone={onSuccess}
      />
    );
  }

  // 2. Confirm Step
  if (step === 'CONFIRM') {
    const confirmItems: ConfirmItem[] = items
      .filter((it) => it.item_name.trim() && it.quantity > 0)
      .map((it) => ({
        label: it.item_name,
        value: formatQty(it.quantity, it.unit),
        sub: it.reason ? `Lý do: ${it.reason}` : undefined,
        badge: warehouse === 'STORE' ? 'Quầy Bar' : 'Kho Tổng',
      }));

    return (
      <ConfirmView
        title="Xác Nhận Hủy / Hỏng Hàng"
        description={`Kiểm tra danh sách các nguyên liệu & bán thành phẩm bị hủy từ ${warehouse === 'STORE' ? 'Quầy Bar / Cửa Hàng' : 'Kho Tổng'}:`}
        items={confirmItems}
        note={note}
        onConfirm={submitWaste}
        onBack={() => setStep('INPUT')}
        isSubmitting={isSubmitting}
        isDisabled={isDisabled}
        confirmText="Xác Nhận Trừ Kho Hủy"
      />
    );
  }

  // 3. Input Step
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={onBack} className="stepper-btn" title="Quay lại" style={{ width: 36, height: 36 }}>
          <ArrowLeft size={18} />
        </button>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 800 }}>Luồng 4: Báo Hủy / Hỏng Hàng</h2>
          <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
            Hủy nguyên liệu thô hoặc BTP đã nấu hỏng
          </span>
        </div>
      </div>

      {/* Warehouse Selector */}
      <div className="card" style={{ padding: 12 }}>
        <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>Chọn Kho Bị Hủy:</label>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 6 }}>
          <button
            type="button"
            className={`btn ${warehouse === 'STORE' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setWarehouse('STORE')}
            style={{ fontSize: 13, minHeight: 40 }}
          >
            Quầy Bar (Cửa Hàng)
          </button>
          <button
            type="button"
            className={`btn ${warehouse === 'CENTRAL' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => {
              setWarehouse('CENTRAL');
              let filtered = false;
              setItems((prev) =>
                prev.map((row) => {
                  const matchMeta = ingredients.find(
                    (i) => i.name.toLowerCase() === row.item_name.toLowerCase()
                  );
                  if (matchMeta && matchMeta.type === 'PREPARED') {
                    filtered = true;
                    return { item_name: '', quantity: 0, unit: 'g', reason: row.reason };
                  }
                  return row;
                })
              );
              if (filtered) {
                toast('Đã xóa món Bán thành phẩm vì Kho Tổng chỉ quản lý Nguyên liệu thô.', { icon: 'ℹ️' });
              }
            }}
            style={{ fontSize: 13, minHeight: 40 }}
          >
            Kho Tổng
          </button>
        </div>
      </div>

      {/* OCR Scan Banner */}
      <div className="card" style={{ padding: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <ShieldAlert size={18} color="var(--primary-accent)" />
          <h3 style={{ fontSize: 14, fontWeight: 700 }}>Quét Phiếu Hủy / Ảnh Hàng Hỏng (OCR AI)</h3>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          <button
            onClick={() => setIsCameraOpen(true)}
            disabled={isScanning}
            className="btn btn-primary"
            style={{ fontSize: 13, minHeight: 44 }}
          >
            <Camera size={16} /> Chụp Ảnh
          </button>

          <label
            className={`btn btn-secondary ${isScanning ? 'opacity-50' : ''}`}
            style={{ fontSize: 13, minHeight: 44, cursor: 'pointer', margin: 0 }}
          >
            <Upload size={16} /> Chọn File
            <input
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              disabled={isScanning}
              onChange={handleFileUpload}
            />
          </label>
        </div>

        {isScanning && (
          <div style={{ textAlign: 'center', padding: '12px 0', color: 'var(--primary-accent)', fontSize: 13 }}>
            <RefreshCw size={18} className="animate-spin" style={{ margin: '0 auto 6px' }} />
            Đang bóc tách phiếu hủy bằng Gemini...
          </div>
        )}
      </div>

      {/* Items List */}
      <div className="card" style={{ padding: 14 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
          <h3 style={{ fontSize: 14, fontWeight: 700 }}>Danh Sách Món Cần Hủy</h3>
          <button onClick={addItem} className="btn btn-secondary" style={{ fontSize: 12, padding: '4px 8px', minHeight: 32 }}>
            <Plus size={14} /> Thêm món
          </button>
        </div>

        {items.length === 0 ? (
          <div
            style={{
              textAlign: 'center',
              padding: '28px 16px',
              borderRadius: 'var(--radius-md)',
              backgroundColor: 'var(--bg-surface-subtle)',
              border: '1.5px dashed var(--border-color)',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 10,
            }}
          >
            <div style={{ fontSize: 28 }}>🗑️</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>
              Chưa có món nào cần hủy
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              Bấm nút bên dưới hoặc Chụp ảnh phiếu hủy để thêm món
            </div>
            <button
              type="button"
              onClick={addItem}
              className="btn btn-primary"
              style={{ minHeight: 38, padding: '6px 16px', fontSize: 13, marginTop: 4 }}
            >
              <Plus size={15} /> Thêm Món Vào Phiếu
            </button>
          </div>
        ) : (
          <>
            {/* Table Header */}
            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 64px 48px 30px', gap: 6, padding: '0 11px', fontSize: 11, fontWeight: 700, color: 'var(--text-tertiary)' }}>
              <div>TÊN NL / BTP HỦY</div>
              <div style={{ textAlign: 'center' }}>SL HỦY</div>
              <div style={{ textAlign: 'center' }}>ĐVT</div>
              <div></div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {items.map((row, idx) => (
            <div
              key={idx}
              style={{
                padding: '8px 10px',
                borderRadius: 'var(--radius-md)',
                backgroundColor: 'var(--bg-surface-subtle)',
                border: '1px solid var(--border-color)',
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
              }}
            >
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'minmax(0, 1fr) 64px 48px 30px',
                  gap: 6,
                  alignItems: 'flex-start',
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <ItemDropdown
                    value={row.item_name}
                    items={warehouse === 'CENTRAL' ? rawIngredients : ingredients}
                    placeholder={warehouse === 'CENTRAL' ? "Chọn nguyên liệu thô..." : "Chọn NL hoặc BTP..."}
                    onChange={(selectedName, defaultUnit) => {
                      setItems((prev) =>
                        prev.map((item, i) =>
                          i === idx
                            ? {
                                ...item,
                                item_name: selectedName,
                                unit: item.unit || defaultUnit || 'g',
                              }
                            : item
                        )
                      );
                    }}
                  />
                </div>

                <input
                  type="number"
                  className="input num"
                  placeholder="SL"
                  value={row.quantity === 0 ? '' : row.quantity}
                  onChange={(e) => updateItem(idx, 'quantity', Number(e.target.value))}
                  style={{
                    textAlign: 'center',
                    fontWeight: 800,
                    fontSize: 14,
                    padding: '4px 2px',
                    minHeight: 38,
                    height: 38,
                    boxSizing: 'border-box',
                  }}
                />

                <UnitDropdown
                  value={row.unit}
                  units={units}
                  onChange={(selectedUnit) => updateItem(idx, 'unit', selectedUnit)}
                />

                {items.length > 1 ? (
                  <button
                    onClick={() => removeItem(idx)}
                    className="stepper-btn"
                    style={{
                      width: 30,
                      minHeight: 38,
                      height: 38,
                      color: 'var(--color-danger)',
                      justifySelf: 'center',
                      padding: 0,
                    }}
                    title="Xóa dòng"
                  >
                    <Trash2 size={16} />
                  </button>
                ) : (
                  <div style={{ width: 30, height: 38 }} />
                )}
              </div>

              <input
                type="text"
                className="input"
                placeholder="Lý do hủy (vd: Quá hạn, Nấu hỏng, Đổ vỡ)..."
                value={row.reason || ''}
                onChange={(e) => updateItem(idx, 'reason', e.target.value)}
                style={{ fontSize: 12, padding: '4px 8px', minHeight: 32 }}
              />
            </div>
          ))}
        </div>
      </>
    )}

        <div style={{ marginTop: 12 }}>
          <label style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)' }}>Ghi chú chung:</label>
          <input
            type="text"
            className="input"
            placeholder="Ví dụ: Sự cố mất điện tủ mát..."
            value={note}
            onChange={(e) => setNote(e.target.value)}
            style={{ marginTop: 4 }}
          />
        </div>
      </div>

      {/* Next to Confirmation Button */}
      <button
        onClick={goToConfirm}
        className="btn btn-primary btn-full"
        style={{ fontSize: 16, minHeight: 52 }}
      >
        <ArrowRight size={18} /> Xem & Xác Nhận Hủy ({items.filter((it) => it.item_name.trim() && it.quantity > 0).length} Món)
      </button>

      {/* Camera Capture Modal */}
      <CameraCapture
        isOpen={isCameraOpen}
        onCapture={(base64) => handleOcr(base64.split(',')[1] || base64)}
        onClose={() => setIsCameraOpen(false)}
      />

      {/* Add Waste Item Modal */}
      <AddWasteItemModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        ingredients={ingredients}
        units={units}
        onAdd={handleAddItem}
      />
    </div>
  );
};
